// TodayTimeline — horizontal "today's focus blocks against the clock"
// strip for the Focus page (W5 demo polish). Renders today's active
// rules as colored bars on a 6 AM → 10 PM ruler with a vertical "now"
// indicator. The block whose [start, end) window contains `now` glows.
//
// Logic lives in utils/focusTimeline.ts (pure, fully unit-tested).
// This file is the rendering layer only.
//
// Display tz: defaults to the browser's resolved IANA. All rule
// start_time/end_time fields are LOCAL clock in their row's timezone;
// today this works for our single-tz seed data but a multi-tz user
// will need a "primary work tz" picker in Phase 2.

import { useEffect, useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import GlassCard from '@/components/shared/GlassCard';
import {
  formatHourTick,
  getTodayTimelineBlocks,
  TIMELINE_TICK_HOURS,
  type TimelineRule,
} from '@/utils/focusTimeline';

type TodayTimelineProps = {
  rules: TimelineRule[];
  /** IANA tz the timeline renders in. Defaults to the browser resolved tz. */
  displayTz?: string;
};

export default function TodayTimeline({ rules, displayTz }: TodayTimelineProps): JSX.Element {
  const tz =
    displayTz && displayTz.length > 0
      ? displayTz
      : (Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'America/Los_Angeles');

  // Ticking `now` so the indicator drifts and the "active block" flips
  // at window boundaries without a page reload. 60s cadence is plenty —
  // sub-minute drift is invisible at this width.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const { blocks, nowPct } = useMemo(
    () => getTodayTimelineBlocks(rules, now, tz),
    [rules, now, tz],
  );

  const showNow = nowPct >= 0 && nowPct <= 100;

  return (
    <GlassCard hover={false} className="overflow-hidden">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-forest" aria-hidden />
          <h2 className="text-sm font-medium text-foreground">Today&apos;s focus blocks</h2>
        </div>
        <span className="text-[10px] font-mono text-muted-foreground/80">{tz}</span>
      </div>

      {/* Ruler strip. Height accommodates: bar (h-10) + bottom hour labels (h-4). */}
      <div className="relative">
        {/* Bar background */}
        <div className="relative h-10 rounded-md bg-secondary/40 border border-border overflow-hidden">
          {/* Hour-tick vertical lines (subtle) */}
          {TIMELINE_TICK_HOURS.map((h) => {
            const leftPct = ((h - TIMELINE_TICK_HOURS[0]) / 16) * 100;
            if (leftPct === 0) return null;
            return (
              <div
                key={`tick-${h}`}
                className="absolute top-0 bottom-0 border-l border-border/40"
                style={{ left: `${leftPct}%` }}
                aria-hidden
              />
            );
          })}

          {/* Blocks */}
          {blocks.map((block) => (
            <div
              key={block.rule.id}
              className={`absolute top-1 bottom-1 rounded-md flex items-center px-1.5 transition-all duration-300 ${
                block.isActive
                  ? 'bg-forest text-primary-foreground shadow-sm ring-2 ring-forest/30 z-10'
                  : 'bg-forest-soft text-forest border border-forest/15'
              } ${block.startsBeforeWindow ? 'rounded-l-none' : ''} ${block.endsAfterWindow ? 'rounded-r-none' : ''}`}
              style={{
                left: `${block.leftPct}%`,
                width: `${block.widthPct}%`,
              }}
              title={`${block.rule.label} · ${block.rule.start_time.slice(0, 5)}–${block.rule.end_time.slice(0, 5)}`}
            >
              <span className="text-[10px] font-medium truncate leading-none">
                {block.rule.label}
              </span>
            </div>
          ))}

          {/* "Now" vertical indicator */}
          {showNow && (
            <div
              className="absolute top-0 bottom-0 z-20 pointer-events-none"
              style={{ left: `${nowPct}%` }}
            >
              <div className="absolute top-0 bottom-0 w-px bg-alert shadow-[0_0_4px_rgba(185,74,60,0.6)]" />
              <div className="absolute -top-1.5 -translate-x-1/2 left-1/2 w-2 h-2 rounded-full bg-alert" />
            </div>
          )}
        </div>

        {/* Hour labels below */}
        <div className="relative h-4 mt-1">
          {TIMELINE_TICK_HOURS.map((h) => {
            const leftPct = ((h - TIMELINE_TICK_HOURS[0]) / 16) * 100;
            return (
              <span
                key={`label-${h}`}
                className="absolute -translate-x-1/2 text-[10px] text-muted-foreground/70 font-mono"
                style={{ left: `${leftPct}%` }}
              >
                {formatHourTick(h)}
              </span>
            );
          })}
          {showNow && (
            <span
              className="absolute -translate-x-1/2 text-[10px] text-alert font-mono font-medium"
              style={{ left: `${nowPct}%` }}
            >
              now
            </span>
          )}
        </div>
      </div>

      {blocks.length === 0 && (
        <p className="mt-3 text-xs text-muted-foreground">No focus blocks scheduled for today.</p>
      )}
    </GlassCard>
  );
}
